import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import TrackRow from "@/components/TrackRow";
import { AlbumCard } from "@/components/MusicCard";
import Section from "@/components/Section";
import { Play, Radar, Sparkles, Check, Trash2, UserPlus, Users } from "lucide-react";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { useMeta } from "@/lib/useMeta";
import { toast } from "sonner";
import ComingSoonCard from "@/components/ComingSoonCard";
import ClaimArtistDialog from "@/components/ClaimArtistDialog";
import ArtistSocials from "@/components/ArtistSocials";
import ScheduleReleaseDialog from "@/components/ScheduleReleaseDialog";
import { heroGlowForArtist } from "@/lib/artistTheme";

const Artist = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { playTracks } = usePlayer();
  const { user, login } = useAuth();

  // AI release tracker state for this artist
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [profile, setProfile] = useState(null);
  const [artwork, setArtwork] = useState(null);
  // Apple Music shows the ~8 most popular tracks with a "Show More"
  // expand. Toggle reveals the full Top Songs catalog (up to 50).
  const [showAllSongs, setShowAllSongs] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/artist/${id}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  // Resolve a profile photo for this artist (Deezer / iTunes / Wikipedia chain)
  useEffect(() => {
    setArtwork(null);
    const name = (typeof window !== "undefined" && window.__artistName_for_artwork) || null;
    // We don't have artistName yet on first render — wait for `data` to load
  }, [id]);

  useEffect(() => {
    if (!data?.artist?.artistName) return;
    api
      .get(`/artwork/artist`, {
        params: { artist_name: data.artist.artistName, artist_id: id },
      })
      .then((r) => {
        if (r.data?.picture) setArtwork(r.data.picture);
      })
      .catch(() => {});
  }, [data?.artist?.artistName, id]);

  // Load any existing AI-discovered upcoming releases for this artist
  useEffect(() => {
    if (!id) return;
    api.get(`/upcoming/feed`, { params: { limit: 100 } })
      .then((r) => {
        const mine = (r.data?.results || []).filter((x) => String(x.artist_id) === String(id));
        setUpcoming(mine);
      })
      .catch(() => {});
    // Artist-scheduled releases (always public)
    api.get(`/upcoming/artist/${id}/scheduled`)
      .then((r) => setScheduled(r.data?.results || []))
      .catch(() => setScheduled([]));
  }, [id]);

  // Load my profile so we can detect "this is MY claimed page"
  useEffect(() => {
    if (!user) { setProfile(null); return; }
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => setProfile(null));
  }, [user]);

  // Check follow status
  useEffect(() => {
    if (!user || !id) { setFollowing(false); return; }
    api.get(`/upcoming/follow/list`)
      .then((r) => {
        const mine = (r.data?.results || []).some((f) => String(f.artist_id) === String(id));
        setFollowing(mine);
      })
      .catch(() => {});
  }, [user, id]);

  // Fetch public follower count for the artist hero. Lives on the upcoming
  // router because it queries the same ``followed_artists`` collection.
  useEffect(() => {
    if (!id) return;
    let cancel = false;
    api.get(`/upcoming/follow/count/${id}`)
      .then((r) => { if (!cancel) setFollowerCount(Number(r.data?.followers ?? 0)); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [id, following]);

  const reloadScheduled = () => {
    api.get(`/upcoming/artist/${id}/scheduled`)
      .then((r) => setScheduled(r.data?.results || []))
      .catch(() => {});
  };

  const deleteScheduled = async (releaseId, title) => {
    if (!window.confirm(`Remove "${title}" from Coming Soon?`)) return;
    try {
      await api.delete(`/upcoming/artist-submit/${releaseId}`);
      toast.success("Removed");
      reloadScheduled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  const handleTrack = async () => {
    if (!user) {
      toast.message("Sign in to track artists", { action: { label: "Sign in", onClick: login } });
      return;
    }
    if (following) {
      try {
        await api.delete(`/upcoming/follow/${id}`);
        setFollowing(false);
        toast.success("Stopped tracking");
      } catch {
        toast.error("Failed to untrack");
      }
      return;
    }
    setScanning(true);
    try {
      await api.post(`/upcoming/follow`, null, {
        params: { artist_id: id, artist_name: data?.artist?.artistName || "" },
      });
      setFollowing(true);
      // Trigger an immediate scan for instant gratification
      const r = await api.post(`/upcoming/scan`, null, {
        params: { artist_id: id, artist_name: data?.artist?.artistName || "", force: true },
      });
      const found = (r.data?.releases || []).length;
      setUpcoming(
        (r.data?.releases || []).map((rel) => ({
          ...rel,
          artist_id: r.data.artist_id,
          artist_name: r.data.artist_name,
          artwork_url: r.data.artwork_url,
        }))
      );
      if (found > 0) {
        toast.success(`Tracking ${data?.artist?.artistName} — found ${found} upcoming release${found > 1 ? "s" : ""}`);
      } else {
        toast.success(`Now tracking ${data?.artist?.artistName}`);
      }
    } catch {
      toast.error("Failed to track");
    } finally {
      setScanning(false);
    }
  };

  useMeta({
    title: data?.artist ? `${data.artist.artistName} · 1o1sound` : "Artist · 1o1sound",
    description: data?.artist
      ? `Listen to ${data.artist.artistName} top tracks and albums on 1o1sound.`
      : undefined,
  });

  if (loading) return <div className="text-white/40">Loading artist...</div>;
  if (!data?.artist) return <div className="text-white/60">Artist not found.</div>;

  const { artist, albums, topSongs, latestAlbums } = data;
  const playable = (topSongs || []).filter((s) => s.previewUrl);
  const songsLimit = showAllSongs ? topSongs.length : 8;

  return (
    <div data-testid="artist-page" className="pb-16">
      <div className="relative -mx-4 sm:-mx-8 -mt-8 px-4 sm:px-8 py-16 mb-8 overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background: heroGlowForArtist(artist.artistName || artist.artistId),
          }}
        />
        <div className="flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-8">
          {artwork && (
            <img
              src={artwork}
              alt={artist.artistName}
              data-testid="artist-hero-artwork"
              className="w-36 h-36 sm:w-48 sm:h-48 rounded-full object-cover shadow-2xl ring-2 ring-white/10 shrink-0"
            />
          )}
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-3">Artist</p>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-semibold tracking-tighter leading-none break-words">{artist.artistName}</h1>
            <div className="flex items-center gap-3 mt-4 flex-wrap text-sm text-white/65">
              <span>{artist.primaryGenreName}</span>
              {followerCount != null && followerCount > 0 && (
                <span
                  data-testid="artist-follower-count"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/[0.06] border border-white/10 rounded-full"
                >
                  <Users size={12} />
                  <b className="text-white">{followerCount.toLocaleString()}</b>
                  <span className="text-white/55">
                    {followerCount === 1 ? "follower" : "followers"}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-6 flex-wrap">
          <button
            data-testid="artist-play-btn"
            onClick={() => playable.length && playTracks(playable, 0)}
            disabled={playable.length === 0}
            className="bg-[#FF2E00] text-white font-semibold rounded-full px-7 py-3 flex items-center gap-2 hover:bg-[#E62900] hover:shadow-[0_0_24px_rgba(255,51,102,0.5)] transition-all disabled:opacity-30"
          >
            <Play size={18} fill="currentColor" /> Play
          </button>
          <button
            data-testid="artist-follow-btn"
            onClick={handleTrack}
            disabled={scanning}
            className={`font-semibold rounded-full px-6 py-3 flex items-center gap-2 transition-all disabled:opacity-60 ${
              following
                ? "bg-white text-black hover:bg-white/90"
                : "bg-white/[0.05] text-white border border-white/15 hover:bg-white/10 hover:border-white/30"
            }`}
            title={following ? "Stop following · AI release tracking off" : "Follow + AI release tracking"}
          >
            {scanning ? (
              <><Sparkles size={16} className="animate-pulse" /> Scanning…</>
            ) : following ? (
              <><Check size={16} /> Following</>
            ) : (
              <><UserPlus size={16} /> Follow</>
            )}
          </button>

          <ClaimArtistDialog
            itunesArtistId={String(artist.artistId)}
            artistName={artist.artistName}
            applePicked={artist.artistLinkUrl || ""}
          />

          {/* Verified artist on this page → schedule release button */}
          {profile?.is_artist &&
            profile?.artist_itunes_id &&
            String(profile.artist_itunes_id) === String(artist.artistId) && (
              <ScheduleReleaseDialog
                artistName={artist.artistName}
                artworkUrl={artist.artworkUrl100}
                onCreated={reloadScheduled}
                testId="artist-schedule-release"
              />
            )}
        </div>
      </div>

      {/* Artist-scheduled releases (always shown when present) */}
      {scheduled.length > 0 && (
        <Section title="Scheduled by the artist" testId="section-artist-scheduled">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scheduled.map((r) => {
              const isMine =
                profile?.user_id && r.submitted_by === profile.user_id;
              return (
                <div
                  key={r.release_id}
                  data-testid={`scheduled-card-${r.release_id}`}
                  className="relative"
                >
                  <ComingSoonCard
                    release={{
                      release_title: r.release_title,
                      release_type: r.release_type,
                      expected_release_date: r.expected_release_date,
                      confidence: 1.0,
                      notes: r.notes,
                      source_urls: r.source_url ? [r.source_url] : [],
                      artist_id: r.artist_id,
                      artist_name: r.artist_name,
                      artwork_url: r.artwork_url,
                      submitted_by_artist: true,
                      collection_name: r.collection_name,
                    }}
                  />
                  {isMine && (
                    <button
                      data-testid={`scheduled-delete-${r.release_id}`}
                      onClick={() => deleteScheduled(r.release_id, r.release_title)}
                      className="absolute top-3 right-3 p-1.5 rounded-full bg-black/60 hover:bg-[#FF2E00]/30 text-white/70 hover:text-[#FF2E00] transition"
                      title="Remove"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {upcoming.length > 0 && (
        <Section title="Coming Soon" testId="section-artist-upcoming">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {upcoming.map((r, i) => (
              <ComingSoonCard key={`${r.release_title}-${i}`} release={r} />
            ))}
          </div>
        </Section>
      )}

      {/* Latest Releases — albums dropped in the last 90 days. Sits
          above "Popular" to match Apple Music's artist-page hierarchy
          where the freshest drop always headlines. */}
      {latestAlbums?.length > 0 && (
        <Section title="Latest Releases" testId="section-artist-latest">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {latestAlbums.map((a) => (
              <AlbumCard key={`latest-${a.collectionId}`} album={a} />
            ))}
          </div>
        </Section>
      )}

      {topSongs?.length > 0 && (
        <Section title="Popular" testId="section-top-songs">
          <div className="flex flex-col">
            {topSongs.slice(0, songsLimit).map((t, i) => (
              <TrackRow key={t.trackId} track={t} index={i} queue={playable} />
            ))}
          </div>
          {topSongs.length > 8 && (
            <button
              data-testid="artist-show-more-songs"
              onClick={() => setShowAllSongs((v) => !v)}
              className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-black text-vermilion hover:text-white transition"
              type="button"
            >
              {showAllSongs ? "Show less" : `Show all ${topSongs.length} songs`}
            </button>
          )}
        </Section>
      )}

      {/* Artist's own uploads (full tracks, not 30s previews) */}
      {data.uploads?.length > 0 && (
        <Section title="Uploaded by the artist" testId="section-artist-uploads">
          <div className="flex flex-col">
            {data.uploads.map((t, i) => (
              <TrackRow
                key={t.trackId}
                track={t}
                index={i}
                queue={data.uploads}
              />
            ))}
          </div>
        </Section>
      )}

      {albums?.length > 0 && (
        <Section title="Albums" testId="section-artist-albums">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {albums.map((a) => (
              <AlbumCard key={a.collectionId} album={a} />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

export default Artist;
